import { Checkbox, Radio, RadioChangeEvent } from 'antd'
import CustomModal from 'components/global/CustomModal'
import { useState } from 'react'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import { CSVLink } from 'react-csv'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import {
  AttendeeStatusParams,
  AttendeeStatusResp,
} from 'types/event/eventAttendance'
import { useGetCheckParticipantsStatus } from 'api/Event/Attendance'
import Spinner from 'components/Spinner'

const ExportCheckInOutModal = ({
  params,
}: {
  params: AttendeeStatusParams
}) => {
  const { checkInOutExportModalOpen, setCheckInOutExportModalOpen } =
    eventAttendanceStore(store => ({
      checkInOutExportModalOpen: store.checkInOutExportModalOpen,
      setCheckInOutExportModalOpen: store.setCheckInOutExportModalOpen,
    }))

  const {
    data: participantStatus,
    isLoading: participantStatusLoading,
    isFetching: participantStatusFetching,
  } = useGetCheckParticipantsStatus(params)
  const [fileFormat, setFileFormat] = useState('csv')
  const [selectedColumns, setSelectedColumns] = useState([
    'First Name',
    'Last Name',
    'Check-In Status',
  ])

  const headers = [
    { label: 'Key', key: 'key' },
    { label: 'First Name', key: 'firstName' },
    { label: 'Last Name', key: 'lastName' },
    { label: 'Check-In Status', key: 'checkInStatus' },
  ]

  const handleFormatChange = (e: RadioChangeEvent) => {
    setFileFormat(e.target.value)
  }

  const handleColumnChange = (checkedValue: boolean, label: string) => {
    setSelectedColumns(prev =>
      checkedValue ? [...prev, label] : prev.filter(col => col !== label),
    )
  }

  const csvDataGenerator = (data: AttendeeStatusResp) => {
    const collectiveMemebers = [
      ...data.kids,
      ...data.authorizedAdults,
      ...data.guardians,
      ...data.dropinAttendees,
    ]
    return collectiveMemebers.map(member => ({
      key: member?._id,
      firstName: member?.firstName,
      lastName: member?.lastName,
      checkInStatus: member?.checkInStatus,
    }))
  }

  const csvData = csvDataGenerator(participantStatus)

  const handleExport = () => {
    const filteredHeaders = headers.filter(header =>
      selectedColumns.includes(header.label),
    )
    const filteredData = csvData.map(row => {
      const filteredRow = {}
      filteredHeaders.forEach(header => {
        filteredRow[header.key] = row[header.key]
      })
      return filteredRow
    })

    if (fileFormat === 'csv') {
      document.getElementById('csv-download-link').click()
    } else if (fileFormat === 'excel') {
      const ws = XLSX.utils.json_to_sheet(filteredData)
      const profilePhotoColIndex = filteredHeaders.findIndex(
        header => header.key === 'profilePhoto',
      )
      if (profilePhotoColIndex !== -1) {
        for (let i = 2; i <= filteredData.length + 1; i++) {
          const cellRef = XLSX.utils.encode_cell({
            r: i - 1,
            c: profilePhotoColIndex,
          })
          const cellValue = ws[cellRef]?.v
          if (cellValue) {
            ws[cellRef] = {
              f: `HYPERLINK("${cellValue}", "Profile Photo")`,
            }
          }
        }
      }

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, 'Event_Attendee.xlsx')
    } else if (fileFormat === 'pdf') {
      const doc = new jsPDF()
      const tableColumn = filteredHeaders.map(header => header.label)
      const tableRows = filteredData.map(row =>
        filteredHeaders.map(header => row[header.key]),
      )

      ;(doc as any).autoTable({
        head: [tableColumn],
        body: tableRows,
      })

      doc.save('Event_Attendee.pdf')
    }
    setCheckInOutExportModalOpen(false)
  }

  if (participantStatusLoading || participantStatusFetching) {
    return (
      <div className="flex justify-center align-center py-40px">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <CustomModal
        openModal={checkInOutExportModalOpen}
        closeModal={setCheckInOutExportModalOpen}
        width={600}
        title="Export Check-in/out data"
        footer={false}
      >
        <div className="formatText ">Format </div>
        <Radio.Group onChange={handleFormatChange} value={fileFormat}>
          <Radio value="csv">CSV</Radio>
          <Radio value="excel">Excel</Radio>
        </Radio.Group>
        <div className="radioText mt-15px">
          Please select columns to export{' '}
        </div>
        <div className="checkbox-group">
          {['First Name', 'Last Name', 'Check-In Status'].map(label => (
            <div key={label}>
              <Checkbox
                checked={selectedColumns.includes(label)}
                onChange={e => handleColumnChange(e.target.checked, label)}
              >
                {label}
              </Checkbox>
            </div>
          ))}
        </div>
        <div className="flex justify-end w-full mt-10px">
          <div className="bg-grey radius-8 pointer mr-30px">
            <button
              className="prim-sm-btn"
              style={{ background: '#EBEDF1', color: 'black' }}
              onClick={() => setCheckInOutExportModalOpen(false)}
            >
              Cancel
            </button>
          </div>
          <button className="prim-sm-btn" onClick={handleExport}>
            Export
          </button>
        </div>
        <CSVLink
          data={csvData}
          headers={headers.filter(header =>
            selectedColumns.includes(header.label),
          )}
          filename="Event_Attendee.csv"
          id="csv-download-link"
          style={{ display: 'none' }}
        />
      </CustomModal>
    </>
  )
}

export default ExportCheckInOutModal
